import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMoreRounded } from "@mui/icons-material";
import { format, isValid, parse } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { dateLocale } from "i18n";

import { useDrugAlias, useDrugLabel, useDrugReference } from "hooks/useDrugReference";

// The label sections shown, in reading order, under patient-facing titles. The keys are the
// `sections` the sync stores (functions/src/openfda/openfdaClient.js). The boxed warning is
// not here: it gets an alert of its own, above everything else.
const SECTIONS = [
  // `title` is a key under info.sections in the medications namespace.
  { key: "indicationsAndUsage", title: "indicationsAndUsage", open: true },
  { key: "description", title: "description" },
  { key: "patientInformation", title: "patientInformation" },
  { key: "dosageAndAdministration", title: "dosageAndAdministration" },
  { key: "dosageFormsAndStrengths", title: "dosageFormsAndStrengths" },
  { key: "contraindications", title: "contraindications" },
  { key: "warningsAndCautions", title: "warningsAndCautions" },
  { key: "adverseReactions", title: "adverseReactions" },
  { key: "drugInteractions", title: "drugInteractions" },
  { key: "mechanismOfAction", title: "mechanismOfAction" },
];

// The sync keeps the first 15,000 characters of each section (SECTION_MAX_CHARS); a section
// that long was cut, and says so rather than ending mid-sentence without explanation.
const SECTION_MAX_CHARS = 15000;

// The label's own numbering and heading at the start of a section: "1 INDICATIONS AND USAGE".
const LEADING_HEADING =
  /^\s*(?:\d+(?:\.\d+)*\s+)?(?:BOXED WARNING|WARNING|INDICATIONS (?:AND|&) USAGE|DOSAGE (?:AND|&) ADMINISTRATION|DOSAGE FORMS (?:AND|&) STRENGTHS|CONTRAINDICATIONS|WARNINGS (?:AND|&) PRECAUTIONS|WARNINGS|ADVERSE REACTIONS|DRUG INTERACTIONS|DESCRIPTION|MECHANISM OF ACTION|INFORMATION FOR PATIENTS|PATIENT INFORMATION|MEDICATION GUIDE)\s*:?\s*/;

const cleanSection = (text) => String(text || "").replace(LEADING_HEADING, "").trim();

/** "Nonsteroidal Anti-inflammatory Drug [EPC]" → "Nonsteroidal Anti-inflammatory Drug". */
const bareClass = (c) => String(c).replace(/\s*\[[^\]]*\]\s*$/, "");

/** openFDA's "20240821" → "Aug 21, 2024". */
const labelDate = (effectiveTime, t) => {
  const parsed = parse(String(effectiveTime || ""), "yyyyMMdd", new Date());
  return isValid(parsed)
    ? format(parsed, t("common.dateFormat"), { locale: dateLocale() })
    : null;
};

const Fact = ({ label, children }) =>
  children ? (
    <Typography variant="body2" color="text.secondary">
      <strong>{label}:</strong> {children}
    </Typography>
  ) : null;

/**
 * The ⓘ popup on a medication card: what the FDA label says about the drug, from the drug
 * reference database (functions/src/openfda). Found by the medication's stored rxcui, or by
 * its name through the brand-name aliases when it was typed by hand.
 */
const MedicationInfoDialog = ({ open, onClose, medication }) => {
  const { t } = useTranslation("medications");
  const { reference, loading } = useDrugReference(medication?.rxcui, medication?.name);
  const { label, loading: labelLoading } = useDrugLabel(reference?.labelSetId);
  // How a hand-typed name was matched, so the popup can say whose label this is.
  const { alias } = useDrugAlias(medication?.rxcui, medication?.name);

  const busy = loading || labelLoading;
  const classes = [
    ...new Set([...(label?.classes || []), ...(label?.pharmClassEpc || []).map(bareClass)]),
  ];
  const strengths = reference?.strengths || [];
  const shown = SECTIONS.filter((s) => cleanSection(label?.sections?.[s.key]));
  const boxedWarning = cleanSection(label?.sections?.boxedWarning);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {medication?.name}
        {reference?.genericName &&
          reference.genericName.toLowerCase() !== String(medication?.name).toLowerCase() && (
            <Typography variant="body2" color="text.secondary">
              {reference.genericName}
            </Typography>
          )}
      </DialogTitle>

      <DialogContent dividers>
        {busy ? (
          <CircularProgress size={24} />
        ) : !label ? (
          <Alert severity="info">
            {t("info.noLabel")}
            {!medication?.rxcui && t("info.noLabelHint")}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {alias?.referenceKey && alias.meaning && (
              <Alert severity="info">
                <Trans
                  ns="medications"
                  i18nKey="info.aliasRecognized"
                  values={{
                    name: medication.name,
                    meaning: alias.meaning,
                    brandNote: alias.country
                      ? t("info.brandSoldIn", { country: alias.country })
                      : "",
                  }}
                  components={{ 1: <strong /> }}
                />
                {/* A registry's licence requires citing it, with its update date. */}
                {alias.attribution && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {t("info.source", { source: alias.attribution })}
                  </Typography>
                )}
              </Alert>
            )}

            {boxedWarning && (
              <Alert severity="warning">
                <AlertTitle>{t("common.boxedWarning")}</AlertTitle>
                <Box sx={{ whiteSpace: "pre-line" }}>{boxedWarning}</Box>
              </Alert>
            )}

            <Stack spacing={0.5}>
              <Fact label={t("info.genericName")}>{reference?.genericName}</Fact>
              <Fact label={t("info.drugClass")}>{classes.join(", ")}</Fact>
              <Fact label={t("info.comesAs")}>
                {strengths.slice(0, 8).join(", ")}
                {strengths.length > 8 ? ", …" : ""}
              </Fact>
              <Fact label={t("info.maxDailyDose")}>
                {reference?.maxDailyDoseMg != null
                  ? t("info.mgPerDay", { mg: reference.maxDailyDoseMg })
                  : null}
              </Fact>
              <Fact label={t("info.manufacturer")}>{label.manufacturerNames?.[0]}</Fact>
              <Fact label={t("info.labelVersion")}>{labelDate(label.effectiveTime, t)}</Fact>
            </Stack>

            <Box>
              {shown.map((section) => {
                const text = cleanSection(label.sections[section.key]);
                const cut = String(label.sections[section.key]).length >= SECTION_MAX_CHARS;
                return (
                  <Accordion key={section.key} defaultExpanded={!!section.open} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                      <Typography variant="subtitle2">{t(`info.sections.${section.title}`)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {text}
                        {cut && t("info.continued")}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>

            <Box>
              {label.dailymedUrl && (
                <Link href={label.dailymedUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                  {t("info.readFullLabel")}
                </Link>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {t("info.disclaimer")}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationInfoDialog;
